<template>
  <div>
    <FiPageRail title="Vehicle Sync History">
      <template #actions>
        <span class="actuals-header-actions">
          <b-button size="sm" variant="success"
                    title="Download CSV for the current range and filters"
                    aria-label="Download CSV for the current range and filters"
                    v-on:click.prevent="download()">
            <i class="fa fa-download" aria-hidden="true"></i>
          </b-button>
          <vsm-sync-history-help />
        </span>
      </template>
    </FiPageRail>

    <!-- one row per status change made by a sync process, whichever pair of systems it moved
         between. answers "why did this bus come off the board on Tuesday" without reading logs -->
    <!-- laid out like the actuals toolbar: a Range label with preset chips and a
         Custom toggle on the first row, the source chips on the second. Native date
         inputs rather than the date-picker component - the calendar opens reliably
         and needs no config to do it. -->
    <div class="actuals-cm-toolbar vsh-toolbar">
      <div class="actuals-date-presets" role="group" aria-label="Date range">
        <span class="actuals-range-label">Range</span>
        <button v-for="opt in RANGES.filter(r => r.value !== 'custom')" :key="'qr-' + opt.value"
                type="button"
                :class="['actuals-date-chip', {'actuals-date-chip--active': active_range_days === opt.value}]"
                :aria-pressed="active_range_days === opt.value ? 'true' : 'false'"
                :title="opt.title"
                v-on:click.prevent="set_range(opt.value)">{{ opt.label }}</button>
      </div>

      <div class="actuals-filter-dates actuals-filter-dates--open">
        <span class="actuals-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn"
                      title="Shift back one window" v-on:click.prevent="nudge_range_days(-day_count)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn"
                      title="Previous day" v-on:click.prevent="nudge_range_days(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="actuals-filter-field">
            <span class="actuals-range-label">From</span>
            <b-form-input v-model="from_date_iso" type="date" size="sm" class="actuals-filter-date"/>
          </span>
        </span>
        <span class="actuals-filter-dateunit">
          <span class="actuals-filter-field">
            <span class="actuals-range-label">To</span>
            <b-form-input v-model="to_date_iso" type="date" size="sm" class="actuals-filter-date"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn"
                      title="Next day" v-on:click.prevent="nudge_range_days(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn"
                      title="Shift forward one window" v-on:click.prevent="nudge_range_days(day_count)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
        <b-form-select size="sm" v-model="selected_vehicle" :options="vehicle_options"
                       class="toolbar-select" @change="load()"/>
      </div>
    </div>

    <div class="actuals-cm-toolbar vsh-toolbar vsh-source-row">
      <div class="actuals-date-presets" role="group" aria-label="Sync sources">
        <span class="actuals-range-label">Sources</span>
        <button type="button"
                :class="['actuals-date-chip', {'actuals-date-chip--active': selected_source === ''}]"
                v-on:click.prevent="filter_by_source('')">All {{ items_full.length }}</button>
        <button v-for="source in visible_sources" :key="source.value" type="button"
                :class="['actuals-date-chip', source.css,
                         {'actuals-date-chip--active': selected_source === source.value}]"
                v-on:click.prevent="filter_by_source(source.value)">{{ source.from }} <i class="fa fa-long-arrow-right vsh-arrow" aria-hidden="true"></i> {{ source.to }} {{ source_counts[source.value] || 0 }}</button>
      </div>
    </div>

    <b-table :busy="loading" :sticky-header="windowHeight ? windowHeight*.9 + 'px':'80vh'" bordered
             :items="items" :fields="fields" :current-page="currentPage" :per-page="perPage">

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
        </div>
      </template>

      <!-- bootstrap-vue rc.20: slot="field" is the syntax this version understands.
           #cell(field) renders nothing at all, silently. -->
      <template slot="event_time" slot-scope="row">
        {{ display_time(row.item.event_time) }}
      </template>

      <!-- same class as the chip, so a row and the filter that selects it read as one -->
      <template slot="source_label" slot-scope="row">
        <span class="vsh-src" :class="source_css(row.item.source)">{{ row.item.source_label }}</span>
      </template>

      <template slot="change" slot-scope="row">
        <span v-if="row.item.from_value">{{ row.item.from_value }} &rarr; </span>
        <strong>{{ row.item.to_value }}</strong>
      </template>
    </b-table>

    <div v-if="!loading && !items.length" class="vst-history-empty">
      No sync activity in this range.
    </div>
  </div>
</template>

<script>

import {api_url, get_vehicle_list, get_vehicle_sync_history} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment';
import axios from "axios";
import { RANGES } from '../utils/date_ranges'
import VsmSyncHistoryHelp from './vsm_sync_history_help.vue'

// every sync that writes a history row, with the tenant flag that turns it on. A tenant
// running two of these should not be offered chips for the other three - the filter would
// only ever return nothing, and it implies we sync things for them that we do not.
// Values match VehicleStatusManager._SYNC_SOURCE_LABELS; flags match the handler gates.
const SYNC_SOURCES = [
  { value: 'DVIR_TO_FLEETIO', from: 'DVIR', to: 'Fleetio', css: 'vsh-src-dvir-fleetio', feature: 'vehicle_status_management_fleetio_dvir_enabled' },
  { value: 'FLEETIO_TO_TBN', from: 'Fleetio', to: 'TBN', css: 'vsh-src-fleetio-tbn', feature: 'vehicle_status_management_fleetio_to_tbn_enabled' },
  { value: 'DVIR_TO_VSM', from: 'DVIR', to: 'VSM', css: 'vsh-src-dvir-vsm', feature: 'vehicle_status_management_dvir_enabled' },
  { value: 'TBN_TO_VSM', from: 'TBN', to: 'VSM', css: 'vsh-src-tbn-vsm', feature: 'vehicle_status_management_tbn_status_enabled' },
  { value: 'MAINTAINX_TO_TBN', from: 'MaintainX', to: 'TBN', css: 'vsh-src-maintainx', feature: 'vehicle_status_management_maintainx_to_tbn_enabled' },
]

export default {
  name: 'vehicle_sync_history',
  components: { VsmSyncHistoryHelp },
  metaInfo: {
    title: "Fleet Integrations",
    titleTemplate: "Fleet Integrations",
  },
  data() {
    return {
      windowHeight: window.innerHeight,
      from_date_iso: moment().add(-7, 'days').format('YYYY-MM-DD'),
      to_date_iso: moment().format('YYYY-MM-DD'),
      selected_vehicle: '',
      selected_source: '',
      RANGES,
      vehicle_options: [{ value: '', text: 'All vehicles' }],
      items: [],
      items_full: [],
      loading: false,
      currentPage: 1,
      perPage: 200,
      // the config the actuals reports use - the picker needs more than a format
      // string to behave (clear/close controls and a bounded range)
      fields: [
        { key: 'event_time', label: 'When', sortable: true },
        { key: 'vehicle_name', label: 'Vehicle', sortable: true },
        { key: 'source_label', label: 'Sync', sortable: true },
        { key: 'action_label', label: 'Change', sortable: true },
        { key: 'change', label: 'From / To' },
        { key: 'detail', label: 'Detail' },
      ],
    }
  },
  computed: {
    // user lives under the profile module - mapState(['user']) reads state.user and is
    // always undefined, which fails silently in a method and fatally in a computed
    ...mapState({
      user: state => state.profile.user,
    }),
    features() { return this.user.features[this.user.tenant] || {} },
    source_options() { return SYNC_SOURCES.filter(s => !!this.features[s.feature]) },
    // one source needs no filter, and none would be a row of nothing. Returned as an empty
    // list rather than hidden behind a wrapping <template v-if>: a bare template in the
    // default slot alongside a v-slot template stops the named slot reaching the child.
    visible_sources() { return this.source_options.length > 1 ? this.source_options : [] },
    show_source_chips() { return this.visible_sources.length > 0 },
    // the window currently loaded, in days
    day_count() {
      return Math.max(1, moment(this.to_date_iso).diff(moment(this.from_date_iso), 'days'))
    },
    // a preset is "active" when the loaded window happens to match it, so a hand-picked
    // range lights nothing rather than needing a mode to sit in
    active_range_days() {
      if (this.to_date_iso !== moment().format('YYYY-MM-DD')) return null
      const match = RANGES.find(r => r.value !== 'custom' && r.value === this.day_count)
      return match ? match.value : null
    },
    // counted off the unfiltered set, so picking one source does not zero the rest
    source_counts() {
      return this.items_full.reduce((acc, row) => {
        acc[row.source] = (acc[row.source] || 0) + 1
        return acc
      }, {})
    },
  },
  watch: {
    from_date_iso() { this.load() },
    to_date_iso() { this.load() },
  },
  mounted() {
    this.load_vehicles()
    this.load()
  },
  methods: {
    display_time(value) {
      return value ? moment.utc(value).local().format('MM-DD-YYYY HH:mm') : ''
    },
    set_range(value) {
      this.from_date_iso = moment().add(-value, 'days').format('YYYY-MM-DD')
      this.to_date_iso = moment().format('YYYY-MM-DD')
    },
    nudge_range_days(days) {
      this.from_date_iso = moment(this.from_date_iso).add(days, 'days').format('YYYY-MM-DD')
      this.to_date_iso = moment(this.to_date_iso).add(days, 'days').format('YYYY-MM-DD')
    },
    source_css(source) {
      const match = SYNC_SOURCES.find(s => s.value === source)
      return match ? match.css : ''
    },
    // filtering by source happens here rather than at the API, so every chip can show
    // its own count against the same set of rows
    apply_source_filter() {
      this.items = this.selected_source
        ? this.items_full.filter(r => r.source === this.selected_source)
        : this.items_full
    },
    filter_by_source(source) {
      this.selected_source = source
      this.apply_source_filter()
    },
    clear_filters() {
      this.selected_source = ''
      this.selected_vehicle = ''
      this.load()
    },
    async load_vehicles() {
      try {
        // the networking layer resolves to {value: <body>} - reading response.data
        // gets undefined, and the .results off it throws straight into the catch
        const { value: { data = [] } } = await get_vehicle_list(this.user.tenant)
        const names = (data || []).map(v => v.name || v.text || v.value).filter(Boolean).sort()
        this.vehicle_options = [{ value: '', text: 'All vehicles' }]
            .concat(names.map(n => ({ value: n, text: n })))
      } catch (e) {
        // the vehicle picker is a convenience; the report still works without it
      }
    },
    async load() {
      this.loading = true
      try {
        const { value: { data = {} } } = await get_vehicle_sync_history(
            this.user.tenant,
            this.selected_vehicle,
            '',
            '',
            this.from_date_iso,
            this.to_date_iso)
        this.items_full = data.results || []
        this.apply_source_filter()
      } catch (e) {
        // carry the reason: a bare toast hides whether the request failed, the reply
        // was the wrong shape, or the call never left the page at all
        this.$emit('showAlert', 'sync history could not be loaded. ' + ((e && e.message) || ''), 'danger')
        this.items_full = []
        this.items = []
      } finally {
        this.loading = false
      }
    },
    async download() {
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/reports/vehiclestatus/sync-history/download`,
          {
            tenant: this.user.tenant,
            vehicleName: this.selected_vehicle,
            filter: '',
            sourceFilter: this.selected_source,
            startDate: this.from_date_iso,
            endDate: this.to_date_iso,
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `vehicle-sync-history-${this.from_date_iso}-${this.to_date_iso}.csv`)
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(() => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
  },
}
</script>

<style scoped>
.vsh-range-label,
.vsh-group-label {
  font-size: 12px;
  opacity: 0.7;
  padding-left: 8px;
}

/* one colour per sync, worn by both the chip and the row's Sync cell */
/* the actuals toolbar classes are inline-flex and take their alignment from an
   ancestor this page does not have, so it is set here rather than inherited */
.vsh-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  gap: 8px;
  padding: 4px 12px;
}

.vsh-arrow {
  opacity: 0.65;
  margin: 0 1px;
}

.vsh-src {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  border: 1px solid transparent;
}
/* the chips wear .actuals-date-chip since the toolbar rebuild, so each colour has to
   name that class too - the .vsm-chip selectors these rules used to carry matched
   nothing here and the chips came back grey. :not(--active) leaves the toolbar's own
   solid-blue selected state visible, the same one the Range presets use. */
.actuals-date-chip:not(.actuals-date-chip--active).vsh-src-dvir-fleetio,
.vsh-src.vsh-src-dvir-fleetio { background-color: hsla(32, 82%, 56%, 0.45); border-color: #d4924a; }
.actuals-date-chip:not(.actuals-date-chip--active).vsh-src-fleetio-tbn,
.vsh-src.vsh-src-fleetio-tbn { background-color: hsla(210, 100%, 50%, 0.35); border-color: #5a9fd4; }
.actuals-date-chip:not(.actuals-date-chip--active).vsh-src-dvir-vsm,
.vsh-src.vsh-src-dvir-vsm { background-color: hsla(60, 100%, 50%, 0.45); border-color: #c4c44a; }
.actuals-date-chip:not(.actuals-date-chip--active).vsh-src-tbn-vsm,
.vsh-src.vsh-src-tbn-vsm { background-color: hsla(290, 60%, 70%, 0.4); border-color: #b088c4; }
.actuals-date-chip:not(.actuals-date-chip--active).vsh-src-maintainx,
.vsh-src.vsh-src-maintainx { background-color: hsla(190, 70%, 50%, 0.4); border-color: #5eb8c9; }
/* the source colours are pale, so the muted grey the toolbar gives its chips is too
   light to read on them */
.actuals-date-chip:not(.actuals-date-chip--active)[class*="vsh-src-"] { color: #212529; }

.vsh-range-dates {
  font-size: 14px;
  opacity: 0.8;
}

.vsh-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  position: relative;
}

.vst-history-empty {
  padding: 16px;
  color: #6c757d;
}
</style>
